import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { RotateCw, Search } from 'lucide-react';
import { vipPrivateAPI } from '../../services/api';
import { useBrand } from '../../context/BrandContext';
import { useNotifications } from '../../context/NotificationContext';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Spinner } from '../ui/spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { useToast } from '../ui/toast';
import { cn } from '../../lib/utils';
import { CreatedRange, Pager } from '../vip/bonus_requests/IssuedFilterControls';
import { CancelCell, CommentCell } from '../vip/bonus_requests/IssuedCells';
import {
  bonusRowView, filtersFromSearch, listParams, playerSearchError, searchWithFilters, STATUS_FILTERS,
} from './privateBonusList';

const PAGE_SIZE = 50;
const COLUMNS = ['Added', 'Player', 'Bonus', 'USD', 'Status', 'Comment', ''];

function BonusRow({ row, confirming, cancelling, onAsk, onCancel, onKeep }) {
  const v = bonusRowView(row);
  return (
    <TableRow className={cn(row.status === 'cancelled' && 'opacity-60')}>
      <TableCell className="px-3 py-2 align-top">
        <div className="whitespace-nowrap text-xs font-bold text-ink">{v.added}</div>
        <div className="text-xs font-medium text-ink-mute">{v.addedBy}</div>
        {v.changed && <div className="text-xs font-medium text-ink-mute">{v.changed}</div>}
      </TableCell>
      <TableCell className="px-3 py-2 align-top">
        <Link to={`/vip-private/player/${v.userId}`} className="block text-left" title="Open the player's profile">
          <div className="text-sm font-bold text-brand-blue hover:underline">{v.email || v.userId}</div>
          <div className="text-xs font-medium text-ink-mute">ID {v.userId}</div>
        </Link>
      </TableCell>
      <TableCell className="px-3 py-2 align-top">
        <div className="whitespace-nowrap text-sm font-bold text-ink">{v.amount}</div>
        <div className="text-xs font-medium text-ink-mute">{v.terms} · {v.title}</div>
        {v.bonusId != null && (
          <div className="mt-0.5 font-mono text-xs font-medium text-ink-mute">#{v.bonusId}</div>
        )}
      </TableCell>
      <TableCell className="px-3 py-2 align-top">
        {v.usd ? (
          <>
            <div className="whitespace-nowrap text-sm font-medium text-ink">{v.usd}</div>
            {v.rate && <div className="whitespace-nowrap font-mono text-xs font-medium text-ink-mute">{v.rate}</div>}
          </>
        ) : <span className="text-xs font-medium text-ink-mute">—</span>}
      </TableCell>
      <TableCell className="px-3 py-2 align-top">
        <div className="flex flex-wrap gap-1">
          <Badge variant={v.status.variant}>{v.status.label}</Badge>
          {v.manualApproval && <Badge variant="warning" title="Above the limit: waits for manual approval">Manual approval</Badge>}
        </div>
      </TableCell>
      <TableCell className="max-w-xs px-3 py-2 align-top">
        <CommentCell comment={v.comment} />
      </TableCell>
      <TableCell className="px-3 py-2 text-right align-top">
        <CancelCell view={v} confirming={confirming} cancelling={cancelling} onAsk={onAsk} onCancel={onCancel} onKeep={onKeep} />
      </TableCell>
    </TableRow>
  );
}

// PrivateBonusesPage: the bonuses issued from the private VIP dashboard
// (bi_backoffice.temp_vip_bonuses_hist), newest first, laid out like the "Issued bonuses" tab of
// Bonus Requests. The manager and the admins see the same list; a pending bonus can still be
// cancelled. The filters and page live in the URL (an email search excepted), so a filtered list
// can be linked.
export default function PrivateBonusesPage() {
  const { activeBrand } = useBrand();
  const { vipDataUpdate } = useNotifications();
  const { show, ToastComponent } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = filtersFromSearch(searchParams);
  const { status, page, from, to, q } = filters;
  // an email search is kept here only: the URL holds user IDs, not emails
  const [emailSearch, setEmailSearch] = useState('');
  const search = q || emailSearch;
  const [searchText, setSearchText] = useState(search);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmId, setConfirmId] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);

  const setFilters = (patch) => {
    const next = searchWithFilters(searchParams, { ...filters, page: 1, ...patch });
    if (next.toString() !== searchParams.toString()) setSearchParams(next);
  };

  // The link always says which status it shows (opening the page lands on ?status=pending).
  useEffect(() => {
    if (!searchParams.has('status')) setSearchParams((sp) => searchWithFilters(sp, filtersFromSearch(sp)), { replace: true });
  }, [searchParams, setSearchParams]);

  // The box follows the active search: a user ID from the URL or the email submitted here.
  useEffect(() => { setSearchText(q || emailSearch); }, [q, emailSearch]);

  // Only the latest request may update the list (the filters can change mid-flight).
  const requestIdRef = useRef(0);
  const load = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError('');
    try {
      const res = await vipPrivateAPI.listBonuses(listParams({ status, page, from, to, search }, PAGE_SIZE));
      if (requestId !== requestIdRef.current) return;
      const { items, total } = res.data;
      // the page emptied (cancellations, another filter): step back to the last page with rows
      if (items.length === 0 && total > 0 && page > 1) {
        setSearchParams((sp) => searchWithFilters(sp, { ...filtersFromSearch(sp), page: Math.ceil(total / PAGE_SIZE) }), { replace: true });
        return;
      }
      setData(res.data);
    } catch (e) {
      if (requestId === requestIdRef.current) setError(e?.response?.data?.error || 'Loading the bonuses failed.');
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [status, page, from, to, search, setSearchParams]);

  // a brand switch reloads too: the rows (and the IDs Cancel sends) belong to one brand
  useEffect(() => { load(); }, [load, vipDataUpdate, activeBrand]);

  // applySearch filters by a user ID (digits) or an email; empty clears the filter.
  const applySearch = (e) => {
    e.preventDefault();
    const text = searchText.trim();
    const invalid = playerSearchError(text);
    if (invalid) {
      setError(invalid);
      return;
    }
    const isEmail = text.includes('@');
    setEmailSearch(isEmail ? text : '');
    setFilters({ q: isEmail ? '' : text });
  };

  const cancel = async (id) => {
    setCancellingId(id);
    try {
      const res = await vipPrivateAPI.cancelBonus(id);
      setData((d) => ({ ...d, items: d.items.map((r) => (r.id === id ? { ...r, ...res.data } : r)) }));
      show('Bonus cancelled.', 'success');
      setConfirmId(null);
    } catch (e) {
      show(e?.response?.data?.error || 'Cancelling the bonus failed.', 'error');
    } finally {
      setCancellingId(null);
    }
  };

  const total = data?.total || 0;
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));
  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <div>
        <h1 className="font-display text-2xl font-black text-ink">Bonuses</h1>
        <p className="mt-1 text-sm font-medium text-ink-dim">
          Issued from a player&apos;s profile, newest first. A pending one can still be cancelled before the issuing process picks it up.
        </p>
      </div>

      <Card className="space-y-3 p-4">
        <div className="flex flex-wrap items-center gap-2">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.key || 'all'} type="button" aria-pressed={status === f.key}
              onClick={() => setFilters({ status: f.key })}
              className={cn('rounded-full px-3 py-1.5 text-xs font-bold',
                status === f.key ? 'bg-ink text-surface' : 'bg-surface-2 text-ink-dim hover:text-ink')}
            >
              {f.label}
            </button>
          ))}
          <span className="flex-1" />
          <CreatedRange from={from} to={to} onChange={setFilters} />
          <form onSubmit={applySearch} className="flex items-center gap-2">
            <Input value={searchText} onChange={(e) => setSearchText(e.target.value)} placeholder="User ID or email" aria-label="Filter by user ID or email" className="h-8 w-56 text-xs" />
            <Button type="submit" variant="outline" size="sm" className="gap-1.5"><Search className="h-3.5 w-3.5" /> Filter</Button>
          </form>
          <Button variant="ghost" size="sm" className="gap-1.5" onClick={load} disabled={loading}><RotateCw className="h-3.5 w-3.5" /> Refresh</Button>
        </div>

        {error && <Alert variant="error"><AlertDescription>{error}</AlertDescription></Alert>}
        {loading && !data && <div className="flex justify-center py-10"><Spinner /></div>}
        {data && data.items.length === 0 && <p className="py-6 text-center text-xs font-medium text-ink-mute">No bonuses match.</p>}
        {data && data.items.length > 0 && (
          <div className={cn('overflow-x-auto', loading && 'opacity-60')}>
            <Table>
              <TableHeader>
                <TableRow>
                  {COLUMNS.map((h) => <TableHead key={h || 'actions'} className="h-9 px-3 text-xs">{h}</TableHead>)}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((row) => (
                  <BonusRow
                    key={row.id} row={row}
                    confirming={confirmId === row.id} cancelling={cancellingId === row.id}
                    onAsk={() => setConfirmId(row.id)} onKeep={() => setConfirmId(null)} onCancel={() => cancel(row.id)}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {data && total > 0 && (
          <Pager data={data} page={page} lastPage={lastPage} disabled={loading} onPage={(p) => setFilters({ page: p })} />
        )}
      </Card>
      {ToastComponent}
    </div>
  );
}
